import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Types for workspace state
export interface Account {
  id: string
  name: string
  email: string
  plan: 'starter' | 'pro' | 'enterprise'
  clerkUserId?: string  // Link to Clerk user
  createdAt: string
  // Trial dates are stored at account level (billing is per-account)
  trialStartedAt?: string
  trialEndsAt?: string
}

/**
 * The shop's physical postal address, printed in the footer of every commercial email
 * (MAR-330). Seeded from Shopify's store settings at install (`source: 'shopify'`) and
 * correctable by the merchant (`source: 'merchant'`, which a reinstall never overwrites).
 * Absent means email sends to shoppers hold. Server-stamped: the client never sets
 * `source` or `updatedAt`.
 */
export interface PostalAddress {
  address1: string
  address2?: string
  city: string
  province?: string
  zip?: string
  country: string
  countryCode?: string
  source: 'shopify' | 'merchant'
  updatedAt: string
}

export interface Workspace {
  id: string
  accountId: string
  name: string
  postalAddress?: PostalAddress
  // Standing store facts the assistant may state directly ([STORE CONTEXT])
  storeContext?: {
    shipping?: string
    returns?: string
    supportContact?: string
    notes?: string
  }
  shopify?: {
    storeUrl: string
    shop?: string
    apiKey?: string
    apiSecret?: string
    webhookSecret?: string
    connected: boolean
    isInstalled?: boolean
    installationType?: 'partner_app' | 'custom_app'
    installedAt?: string
    webhooksConfigured?: boolean
    /** The shop's base currency (ISO 4217), written at install. Absent or null = not known. */
    currency?: string | null
  }
  webhooks?: {
    status: 'connected' | 'disconnected' | 'unknown'
    lastChecked?: string
  }
  discountGovernance?: {
    enabled: boolean
    maxDiscountPercent: number
    minCartValue?: number
    codeExpiryHours?: number
    createInShopify?: boolean
    freeShippingEnabled?: boolean
    freeShippingMinOrder?: number
    channelLimits?: {
      chat?: number
      cart_recovery?: number
      post_browse?: number
      inspiration?: number
      email_reply?: number
      sms?: number
    }
    customerTypeLimits?: {
      new?: number
      returning?: number
      vip?: number
    }
    budget?: {
      period?: 'daily' | 'weekly' | 'monthly'
      amount?: number
    }
    escalation?: {
      thresholdPercent?: number
      thresholdCartValue?: number
    }
    /**
     * The keys `discount-governance` actually enforces (MAR-311). Written only through
     * `@marva/shared/discount-governance-form`; the legacy `budget.perCustomerLimit` /
     * `budget.perCustomerPeriod` / top-level `exclusions` were never read by the enforcer.
     */
    customerLimits?: {
      maxPerCustomer?: number
      period?: 'weekly' | 'monthly' | 'yearly'
    }
    productExclusions?: {
      productIds?: string[]
      /** Always written empty until cart items are enriched server-side. */
      categories?: string[]
      tags?: string[]
    }
    // Legacy fields (kept for backwards compatibility)
    dailyBudget?: number
    monthlyBudget?: number
    allowedTriggers?: string[]
  }
  smsConfig?: {
    enabled: boolean
    dailyLimitMessages: number
    monthlyLimitMessages: number
    maxSmsPerCustomerMonth: number
    quietHoursStart: number
    quietHoursEnd: number
    enabledCampaignTypes: string[]
  }
  twilio?: {
    accountSid?: string
    authToken?: string
    messagingServiceSid?: string
    phoneNumber?: string
    configured: boolean
  }
  storeDomain?: {
    archetype: string
    confidence?: number
    categoryDistribution?: Record<string, number>
    catalogCharacteristics?: {
      avgPrice?: number
      skuCount?: number
      hasSizedProducts?: boolean
      hasConsumables?: boolean
    }
    config?: {
      lifecycle: { vip: { minOrders: number; minSpend: number }; loyal: { minOrders: number }; atRiskDays: number; churnedDays: number }
      valueSegments: { vip: number; high: number; medium: number }
      enabledSignals: Record<string, boolean>
      frequency: { emailPerWeek: number; emailPerMonth: number; smsPerMonth: number; minGapHours: number }
      // `marketing` (lapse threshold, inspiration cadence) is legacy since MAR-35; old rows may carry it.
      marketing?: { lapsedDays: number; frequencyCapDays: number; productsPerEmail: number }
      orderIntelligence: { crossSellFraming: string; reorderDueWindowDays: number }
    }
    merchantOverride?: boolean
    classifiedAt?: string
    classifiedBy?: string
  }
  mode?: 'shadow' | 'live'
  /** Suitability capture (ops-set after counsel; dark today). Read-only here. */
  suitability?: {
    enabled?: boolean
    disclosureVersion?: string | null
  }
  // Escalate-and-Return: the agent may escalate a question it can't answer to the
  // merchant, then return their answer to the customer. Set on the Authority tab's `ask_merchant` row (MAR-398;
  // it was the Agent tab).
  // Absent = off.
  askMerchantEnabled?: boolean
  trialStartedAt?: string
  trialEndsAt?: string
  // Email compliance (Phase 12D)
  unsubscribeRedirectUrl?: string
  // Agent settings (discontinued products)
  agentSettings?: {
    mentionDiscontinuedProducts: boolean
  }
  createdAt: string
  updatedAt?: string
}

export interface User {
  id: string
  accountId: string
  workspaceId: string
  email: string
  name: string
  firstName?: string
  lastName?: string
  role: 'super_admin' | 'owner' | 'admin' | 'member'
  clerkUserId?: string  // Link to Clerk user
  isSuperAdmin?: boolean  // True if user has isMarvaAdmin flag in Clerk
}

// New interface for user memberships across multiple accounts/workspaces
export interface UserMembership {
  clerkUserId: string
  isSuperAdmin?: boolean  // True if user has isMarvaAdmin flag in Clerk
  accounts: {
    account: Account
    workspaces: {
      workspace: Workspace
      userRole: 'super_admin' | 'owner' | 'admin' | 'member'
      userId: string
    }[]
  }[]
}

/** One `accounts[].workspaces[]` entry of a `UserMembership`: the caller's row in a workspace. */
export type MembershipEntry = UserMembership['accounts'][number]['workspaces'][number]

/**
 * `membership` with one more of the caller's rows in it — the row a create just wrote.
 *
 * Why this exists: `GET /users/memberships` reads an eventually-consistent index, so right
 * after `POST /workspaces` it can lack the creator's new `owner` row. The create response
 * carries that row, so it is merged in here and the membership is the one place a role is
 * read from — there is no `currentUser` fallback to cover the gap (MAR-250 review round 2).
 *
 * An entry with the same `userId` in the same workspace is replaced (a later load that
 * already has the row must not grow a duplicate); any OTHER row in that workspace is kept,
 * because `roleForWorkspace` resolves duplicates exactly as the server does. The account is
 * added when the membership does not list it yet (a brand-new account).
 */
export function withMembershipRow(
  membership: UserMembership | null,
  account: Account,
  entry: MembershipEntry,
  clerkUserId?: string
): UserMembership {
  const base: UserMembership = membership || { clerkUserId: clerkUserId || '', accounts: [] }
  const sameRow = (e: MembershipEntry) => e.userId === entry.userId && e.workspace?.id === entry.workspace.id
  let found = false
  const accounts = base.accounts.map((acc) => {
    if (acc.account.id !== account.id) return acc
    found = true
    return { ...acc, workspaces: [...acc.workspaces.filter((e) => !sameRow(e)), entry] }
  })
  if (!found) accounts.push({ account, workspaces: [entry] })
  return { ...base, accounts }
}

/**
 * A row a create response carried that the memberships index may not list yet.
 *
 * ⚠️ **Why the store keeps these, rather than merging once.** `withMembershipRow` alone is
 * not enough: the create flows re-read `GET /users/memberships` straight after creating
 * (`CreateWorkspaceModal`, `CreateAccountModal` → `loadWorkspaceData`), and that read is the
 * eventually-consistent index — so a plain `setUserMembership` of it REPLACED the merged row
 * and the creator had no role in the workspace they had just made (MAR-250 review round 3).
 * So every `setUserMembership` re-applies the pending rows of THAT user, and a pending row
 * is dropped as soon as a read includes it — the server has caught up.
 *
 * Scoped by `clerkUserId`: a membership of another user drops them (a user switch), and
 * `clearSession` (sign-out) clears them. Bounded by `PENDING_MEMBERSHIP_TTL_MS`: an index
 * that has not caught up in that long is not lagging, and a row kept past it could keep
 * answering for a membership someone has since removed — the exact stale-role defect this
 * replaced.
 *
 * ⚠️ **A stale-role refusal deletes nothing (PR #237 round 7).** MAR-250 review round 3 found a
 * creator removed or demoted inside the TTL kept answering "owner", because each refresh
 * re-applied the persisted pending row; rounds 3–6 answered it by stripping rows on the
 * refusal, which also locked a REAL owner out (`no_workspace_membership` is what
 * `assertWorkspaceRole`'s lagging `WorkspaceIndex` answers for a row written seconds ago) and
 * an admin out of their own role (`insufficient_workspace_role` is also an owner-only refusal
 * to a correctly cached admin). Now a refusal only triggers `refreshMembership`'s
 * `ClerkUserIndex` read, and that read decides through `applyPendingRows`:
 * - **a demotion is honoured once the re-read reflects it.** `ClerkUserIndex` is itself an
 *   eventually consistent GSI, so the re-read can still list the old role for a while, and
 *   until it catches up the controls stay offered (UI only — the server refuses the write).
 *   When the read lists the row (same `userId`, same workspace) with its new role, a pending
 *   row it lists is dropped as caught up, so the listed role supersedes the pending owner
 *   row — never merged beside it.
 * - **a removal inside the TTL is NOT.** The read omits the row, which is indistinguishable
 *   here from the index still lagging, so the pending row keeps answering "owner" until
 *   `PENDING_MEMBERSHIP_TTL_MS` expires and is then dropped. That is the remaining trade-off,
 *   and it is UI only: every write that creator attempts is still refused server-side by
 *   the same check. MAR-302 makes the server's `no_workspace_membership` a fact; this client
 *   side stays as it is.
 */
export interface PendingMembershipRow {
  clerkUserId: string
  account: Account
  entry: MembershipEntry
  recordedAt: number
}

export const PENDING_MEMBERSHIP_TTL_MS = 5 * 60 * 1000

const samePendingRow = (a: PendingMembershipRow, b: PendingMembershipRow) =>
  a.clerkUserId === b.clerkUserId &&
  a.entry.userId === b.entry.userId &&
  a.entry.workspace?.id === b.entry.workspace?.id

const lists = (membership: UserMembership, row: PendingMembershipRow) =>
  membership.accounts.some((acc) =>
    acc.workspaces.some((e) => e.userId === row.entry.userId && e.workspace?.id === row.entry.workspace?.id)
  )

/**
 * `membership` with this user's still-pending rows merged in, and the pending rows that are
 * still owed afterwards. A row of another user, an expired row, and a row the read already
 * lists are all dropped from what is owed.
 */
export function applyPendingRows(
  membership: UserMembership,
  pending: PendingMembershipRow[],
  now: number = Date.now()
): { membership: UserMembership; pending: PendingMembershipRow[] } {
  const owed = pending.filter(
    (row) =>
      row.clerkUserId === membership.clerkUserId &&
      now - row.recordedAt < PENDING_MEMBERSHIP_TTL_MS &&
      !lists(membership, row)
  )
  const merged = owed.reduce((m, row) => withMembershipRow(m, row.account, row.entry), membership)
  return { membership: merged, pending: owed }
}

interface WorkspaceState {
  // Current session data
  currentAccount: Account | null
  currentWorkspace: Workspace | null
  currentUser: User | null
  
  // Available data for current user
  userMembership: UserMembership | null  // All accounts/workspaces user has access to
  /** Created rows the memberships index may not list yet — see `PendingMembershipRow`. */
  pendingMembershipRows: PendingMembershipRow[]
  /**
   * Which signed-in session this is. Bumped by every `clearSession` that ends a session
   * (sign-out — `shell/EndSessionOnSignOut`, whatever ended Clerk's session — or a user
   * switch) — not persisted. A create captures it before its awaits and
   * passes it to `mergeMembershipRow`, which writes nothing if it changed in between: the
   * session that issued the create is gone (PR #237 round 7).
   */
  sessionGeneration: number
  accounts: Account[]  // All accounts user has access to
  workspaces: Workspace[]  // All workspaces for current account
  users: User[]  // All users in current workspace
  
  // Session management
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Actions
  setCurrentAccount: (account: Account) => void
  setCurrentWorkspace: (workspace: Workspace) => void
  setCurrentUser: (user: User) => void
  setUserMembership: (membership: UserMembership) => void
  /**
   * Record one of `clerkUserId`'s rows a create returned, and merge it into `userMembership`
   * when that is the same user's — see `PendingMembershipRow`.
   *
   * `sessionClerkUserId` is the user THIS session is signed in as. When it is given, equals
   * `clerkUserId`, and no membership is loaded at all (the memberships read failed), the row
   * SEEDS `userMembership` — see the action.
   */
  mergeMembershipRow: (
    account: Account,
    entry: MembershipEntry,
    clerkUserId?: string,
    sessionClerkUserId?: string,
    issuedInGeneration?: number
  ) => void
  /** Forget pending rows the caller knows are gone (a removal that just succeeded). */
  dropPendingMembershipRows: (userIds: string[]) => void
  setAccounts: (accounts: Account[]) => void
  setWorkspaces: (workspaces: Workspace[]) => void
  setUsers: (users: User[]) => void
  setAuthenticated: (authenticated: boolean) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  switchAccount: (accountId: string) => void  // Switch to different account
  switchWorkspace: (workspaceId: string) => void  // Switch to different workspace
  /**
   * Clear the signed-in state. `sameSession: true` is for a re-load that resets the SAME
   * user's data (`loadWorkspaceData`'s no-accounts / failed-read branches) — it keeps
   * `sessionGeneration`, so a create in flight for that user still lands. Every other caller
   * (sign-out, a user switch) ends the session and bumps it.
   */
  clearSession: (options?: { sameSession?: boolean }) => void
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentAccount: null,
      currentWorkspace: null,
      currentUser: null,
      userMembership: null,
      pendingMembershipRows: [],
      sessionGeneration: 0,
      accounts: [],
      workspaces: [],
      users: [],
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setCurrentAccount: (account) => 
        set({ currentAccount: account }),
      
      setCurrentWorkspace: (workspace) => 
        set({ currentWorkspace: workspace }),
      
      setCurrentUser: (user) => 
        set({ currentUser: user }),

      // ⚠️ Takes a SERVER read (or one derived from it): a pending row it already lists is
      // dropped as caught up, so never pass it a membership that already has them merged in.
      setUserMembership: (membership) => {
        // Re-apply this user's created rows a lagging read may not list yet.
        const applied = applyPendingRows(membership, get().pendingMembershipRows)
        set({
          userMembership: applied.membership,
          pendingMembershipRows: applied.pending,
          accounts: applied.membership.accounts.map(acc => acc.account),
        })
      },

      mergeMembershipRow: (account, entry, clerkUserId, sessionClerkUserId, issuedInGeneration) => {
        // ⚠️ The session that issued the create has ended (sign-out or a user switch while it
        // was in flight): its row is not this session's to record, merge or seed, and nothing
        // is written — not even a pending row, which would be persisted (PR #237 round 7).
        if (issuedInGeneration !== undefined && issuedInGeneration !== get().sessionGeneration) return
        const current = get().userMembership
        const owner = clerkUserId || current?.clerkUserId
        if (!owner) return
        const row: PendingMembershipRow = { clerkUserId: owner, account, entry, recordedAt: Date.now() }
        const pending = [...get().pendingMembershipRows.filter((r) => !samePendingRow(r, row)), row]
        // ⚠️ Only into the SAME user's membership. Another user's membership means the row
        // waits, and applies when this user's membership is next set. Merged directly rather
        // than through `setUserMembership`: that prunes rows its input already lists, and its
        // input here is our own merge, not a server read.
        if (current && current.clerkUserId === owner) {
          const merged = withMembershipRow(current, account, entry)
          set({ pendingMembershipRows: pending, userMembership: merged, accounts: merged.accounts.map(acc => acc.account) })
        } else if (
          !current &&
          clerkUserId &&
          sessionClerkUserId &&
          clerkUserId === sessionClerkUserId &&
          issuedInGeneration !== undefined
        ) {
          // ⚠️ SEED (PR #237 round 6). No membership loaded at all means the memberships read
          // failed — and the role is read from the membership alone, so without this the
          // owner who just created a workspace has no role in it: Account, Billing and every
          // Authority tab refuse her until some later read succeeds. The create response is
          // this session's own trusted data about its own user, so it may start the
          // membership. Never for another user's row: `sessionClerkUserId` must be the user
          // who issued the create AND the row's owner — and the session that issued it must
          // still be the signed-in one (`issuedInGeneration`, checked above; required here,
          // since a null membership is exactly what a sign-out mid-create leaves behind).
          const seeded = withMembershipRow(null, account, entry, clerkUserId)
          set({ pendingMembershipRows: pending, userMembership: seeded, accounts: seeded.accounts.map(acc => acc.account) })
        } else {
          set({ pendingMembershipRows: pending })
        }
      },

      dropPendingMembershipRows: (userIds) => {
        const gone = new Set(userIds)
        set({ pendingMembershipRows: get().pendingMembershipRows.filter((r) => !gone.has(r.entry.userId)) })
      },

      setAccounts: (accounts) => 
        set({ accounts }),
      
      setWorkspaces: (workspaces) => 
        set({ workspaces }),
      
      setUsers: (users) => 
        set({ users }),
      
      setAuthenticated: (authenticated) => 
        set({ isAuthenticated: authenticated }),
      
      setLoading: (loading) => 
        set({ isLoading: loading }),
      
      setError: (error) => 
        set({ error }),

      switchAccount: (accountId) => {
        const state = get()
        const account = state.accounts.find(acc => acc.id === accountId)
        if (account) {
          // Get workspaces for this account
          const accountMembership = state.userMembership?.accounts.find(
            acc => acc.account.id === accountId
          )
          const workspaces = accountMembership?.workspaces.map(w => w.workspace) || []
          const firstWorkspace = workspaces[0] || null
          const currentUser = accountMembership?.workspaces[0] ? {
            id: accountMembership.workspaces[0].userId,
            accountId: account.id,
            workspaceId: firstWorkspace?.id || '',
            email: account.email,
            name: 'Current User',
            role: accountMembership.workspaces[0].userRole
          } as User : null

          set({
            currentAccount: account,
            currentWorkspace: firstWorkspace,
            currentUser,
            workspaces,
            users: currentUser ? [currentUser] : []
          })
        }
      },

      switchWorkspace: (workspaceId) => {
        const state = get()
        const workspace = state.workspaces.find(ws => ws.id === workspaceId)
        if (workspace && state.currentAccount) {
          // Find user role in this workspace
          const accountMembership = state.userMembership?.accounts.find(
            acc => acc.account.id === state.currentAccount?.id
          )
          const workspaceMembership = accountMembership?.workspaces.find(
            w => w.workspace.id === workspaceId
          )
          const currentUser = workspaceMembership ? {
            id: workspaceMembership.userId,
            accountId: state.currentAccount.id,
            workspaceId: workspace.id,
            email: state.currentAccount.email,
            name: 'Current User',
            role: workspaceMembership.userRole
          } as User : null

          set({
            currentWorkspace: workspace,
            currentUser,
            users: currentUser ? [currentUser] : []
          })
        }
      },
      
      clearSession: (options) =>
        set({
          sessionGeneration: options?.sameSession ? get().sessionGeneration : get().sessionGeneration + 1,
          currentAccount: null,
          currentWorkspace: null,
          currentUser: null,
          userMembership: null,
          pendingMembershipRows: [],
          accounts: [],
          workspaces: [],
          users: [],
          isAuthenticated: false,
          error: null,
        }),
    }),
    {
      name: 'marva-workspace-storage',
      partialize: (state) => ({
        currentAccount: state.currentAccount,
        currentWorkspace: state.currentWorkspace,
        currentUser: state.currentUser,
        userMembership: state.userMembership,
        // Persisted so a reload inside the lag window does not lose the creator's row.
        pendingMembershipRows: state.pendingMembershipRows,
        accounts: state.accounts,
        workspaces: state.workspaces,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)